import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  Typography,
  UploadFeeIcon,
} from '@acceso/design-system';
import { useTranslation } from 'react-i18next';

import { EconomicalValueChannelSourceType } from '@/features/broadcasters/types';
import { useDisclosureWithData } from '@/hooks/useDisclosureWithData';
import { routerManager } from '@/routes/app-routes';
import { AVRouteEnum } from '@/routes/constants/AVRouteEnum';

import { useDeleteEconomicalValueChannelSource } from '../api/delete-economical-value-channel-source';
import { EconomicalValueChannelSourcesTable } from '../components/EconomicalValueChannelSourcesTable/EconomicalValueChannelSourcesTable';
import { useEconomicalValuePermissions } from '../hooks/useEconomicalValuePermissions';

export default function EconomicalValueChannelSourcesPage() {
  const { t } = useTranslation();
  const { canDelete, canCreate } = useEconomicalValuePermissions();

  const deleteDialog = useDisclosureWithData<EconomicalValueChannelSourceType>();
  const { mutate: deleteChannelSource, isPending: isDeleting } =
    useDeleteEconomicalValueChannelSource();

  const onDelete = (item: EconomicalValueChannelSourceType) => {
    deleteDialog.open(item);
  };

  const handleConfirmDelete = () => {
    if (!deleteDialog.data) return;
    deleteChannelSource(
      { id: deleteDialog.data.id },
      {
        onSuccess: () => {
          deleteDialog.close();
        },
      }
    );
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Fuentes de valor económico')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              onClick={() => routerManager.to(AVRouteEnum.ECONOMIC_VALUE_UPLOAD)}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: UploadFeeIcon,
                },
                text: {
                  value: t('Cargar fichero de tarifas'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <EconomicalValueChannelSourcesTable onDelete={canDelete ? onDelete : undefined} />
      </div>

      <ModalDialog
        isOpen={deleteDialog.isOpen}
        onClose={deleteDialog.close}
        onAction={handleConfirmDelete}
        heading={t('Eliminar fuente de valor económico')}
        actionLabel={isDeleting ? t('Eliminando…') : t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro de que quieres eliminar esta fuente de valor económico?')}
          description={t(
            'Esta acción marcará el registro como eliminado. Los datos de valor económico relacionados no se verán afectados.'
          )}
        />
      </ModalDialog>
    </>
  );
}
